import { Link } from "react-router-dom";

import { type Project, useAdminProjects, useDeleteProject } from "@/lib/queries/use-projects";

function ProjectRow({ project }: { project: Project }) {
  const deleteProject = useDeleteProject();

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete "${project.title}"?`)) {
      deleteProject.mutate(project.id);
    }
  };

  return (
    <div className="p-4 md:py-4 md:px-0 border border-border md:border-0 md:border-b bg-card md:bg-transparent">
      {/* Mobile: Card layout, Desktop: Row layout */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0">
        <div className="flex-1">
          <div className="flex items-start md:items-center gap-2 flex-wrap">
            <Link
              to={`/admin/projects/${project.id}`}
              className="text-[14px] md:text-[15px] font-bold text-text-primary hover:text-primary transition-colors"
            >
              {project.title}
            </Link>
            {!project.visible && (
              <span className="text-[9px] md:text-[10px] text-text-faint tracking-caps uppercase px-1.5 md:px-2 py-0.5 border border-border">
                Hidden
              </span>
            )}
          </div>
          <p className="text-[11px] md:text-[12px] text-text-muted mt-1">
            {project.category || "Uncategorized"} • Order: {project.display_order}
          </p>
        </div>
        <div className="flex items-center gap-4 pt-2 md:pt-0 border-t border-border md:border-0 mt-2 md:mt-0">
          <Link
            to={`/admin/projects/${project.id}`}
            className="text-[11px] md:text-[12px] text-text-muted hover:text-primary py-1"
          >
            Edit
          </Link>
          {project.url && (
            <a
              href={project.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] md:text-[12px] text-text-muted hover:text-primary py-1"
            >
              Visit
            </a>
          )}
          <button
            onClick={handleDelete}
            className="text-[11px] md:text-[12px] text-text-muted hover:text-destructive py-1"
            disabled={deleteProject.isPending}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminProjectsList() {
  const { data: projects, isLoading, error } = useAdminProjects();

  return (
    <div>
      <div className="flex items-center justify-between mb-6 md:mb-8">
        <h1 className="text-[24px] md:text-[28px] font-bold text-primary">Projects</h1>
        <Link
          to="/admin/projects/new"
          className="px-3 md:px-4 py-1.5 md:py-2 bg-primary text-primary-foreground font-bold text-[12px] md:text-[13px] hover:opacity-90 transition-opacity"
        >
          + New
        </Link>
      </div>

      {isLoading && <p className="text-text-muted py-8 text-center">Loading projects...</p>}

      {error && (
        <p className="text-destructive py-8 text-center">
          Failed to load projects. Please try again.
        </p>
      )}

      {projects && projects.length === 0 && (
        <div className="py-12 text-center border border-dashed border-border">
          <p className="text-text-muted mb-4">No projects yet</p>
          <Link to="/admin/projects/new" className="text-[13px] text-primary hover:underline">
            Add your first project
          </Link>
        </div>
      )}

      {projects && projects.length > 0 && (
        <div className="space-y-3 md:space-y-0 md:border-t md:border-border">
          {projects.map((project) => (
            <ProjectRow key={project.id} project={project} />
          ))}
        </div>
      )}
    </div>
  );
}
